import { Observable } from 'rxjs';
import { AnalysisNotPossibleDialogComponent } from '../../../components/dialogs/analysis-not-possible-dialog/analysis-not-possible-dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { PathResolveService } from '../../../services/path-resolve.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AnalysisType, ParentNavigationService } from './parent-navigation-service';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { EvaluationGenerationDto } from 'src/app/data-transfer/entities/evaluation-entities/evaluation-generation-dto';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { RecyclabilityResultDto } from 'src/app/data-transfer/entities/evaluation-entities/recyclability-result-dto';
import { LifeCycleResultDto } from 'src/app/data-transfer/entities/evaluation-entities/life-cycle-result-dto';
import { ProblemInformationDto } from 'src/app/data-transfer/entities/evaluation-entities/problem-information-dto';
import moment from 'moment/min/moment-with-locales';
import { CreditsService } from 'src/app/services/credits-service';

@Injectable({ providedIn: 'root' })
export class PackagingUnitNavigationService extends ParentNavigationService {

  constructor(
    protected dialog: MatDialog,
    protected translateService: TranslocoService,
    protected analysisApiService: AnalysisApiService,
    protected creditsService: CreditsService,
    private router: Router,
    private spinner: NgxSpinnerService
  ) {
    super(dialog, translateService, analysisApiService, creditsService);
  }

  navigateToPackagingUnit(packagingUnitId: number, packagingUnitVersion?: number) {
    const path = packagingUnitVersion == null ?
      PathResolveService.getUpdatePackagingUnitPath(true, packagingUnitId) :
      PathResolveService.getPackagingUnitHistoryPath(true, packagingUnitId, packagingUnitVersion);
    this.router.navigate([path]);
  }

  async navigateToAnalysisWithCostConfirmation(
    analysisType: AnalysisType, packagingUnitId: number, versionId: number,
    problemInformation: ProblemInformationDto[]
  ) {
    const isAnalysisPossible = problemInformation.length == 0;
    await this.creditsService.setCreditsCount();

    this.getAnalysisCost(analysisType, PackagingPart.Unit, packagingUnitId, versionId).subscribe(cost => {
      const availableCredits = this.getAvailableCreditsForAnalysis(analysisType);
      if (cost > availableCredits) {
        this.stopWhenCreditsInsufficient(availableCredits, cost);
        this.spinner.hide();
        return;
      }

      if (!isAnalysisPossible) {
        this.stopWhenAnalysisNotPossible(problemInformation);
      } else {
        this.navigateToAnalysis(analysisType, packagingUnitId, versionId);
      }
    });
  }

  private navigateToAnalysis(
    analysisType: AnalysisType, packagingUnitId: number, versionId: number
  ) {
    let observableAnalysis: Observable<LifeCycleResultDto[] | RecyclabilityResultDto[]> | null = null;
    const dto: EvaluationGenerationDto = { id: packagingUnitId, version: versionId };
    if (analysisType === AnalysisType.Recyclability) {
      observableAnalysis = this.analysisApiService.getRecyclabilityEvaluationResult(dto, PackagingPart.Unit);
    } else if (analysisType === AnalysisType.Lifecycle) {
      observableAnalysis = this.analysisApiService.getLifeCycleEvaluationResult(dto, PackagingPart.Unit);
    } else if (analysisType === AnalysisType.CradleToGateLifecycle) {
      observableAnalysis = this.analysisApiService.getCradleToGateEvaluationResult(dto, PackagingPart.Unit);
    } else if (analysisType === AnalysisType.ExpenseCalculation) {
      dto.evaluationReferenceTimestamp = moment().utc().format("yyyy-MM-DDTHH:mm:ss+00:00")
      const observableExpenseResult = this.analysisApiService.getExpenseCalculationResults(dto, PackagingPart.Unit);
      this.evaluationSubscription = observableExpenseResult.subscribe(analyses => {
        this.navigateToHistoryAnalysis(analysisType, packagingUnitId, versionId, [analyses[0].licenseFee?.id,
                                                                                  analyses[0].ecomodulation?.id,
                                                                                  analyses[0].plasticTax?.id]);
      });
    }

    if (!observableAnalysis) { return; }
      this.evaluationSubscription = observableAnalysis.subscribe(analyses => {
        this.navigateToHistoryAnalysis(analysisType, packagingUnitId, versionId, [analyses[0].id ?? '']);
        this.creditsService.setCreditsCount();
      });
  }

  private stopWhenAnalysisNotPossible(problemInformation: ProblemInformationDto[]): void {
    this.spinner.hide();
    const generalExplanation = this.translateService.translate("analysisNotPossibleInfo.generalConditionsUnit");
    const alertDialogConfig = getDialogConfig({ generalExplanation: generalExplanation, problemInformation: problemInformation }, '600px');
    this.dialog.open(AnalysisNotPossibleDialogComponent, alertDialogConfig);
  }

  navigateToHistoryAnalysis(
    analysisType: AnalysisType, packagingId: number, packagingVersionId: number, analysisVersionsId: (string | undefined)[]
  ) {
    switch (analysisType) {
      case AnalysisType.Recyclability: {
        this.router.navigate([PathResolveService.getRecyclabilityAnalysisHistoryPathUnit(
          true, packagingId, packagingVersionId, analysisVersionsId[0] ?? '')]);
        break;
      }
      case AnalysisType.CradleToGateLifecycle: {
        this.router.navigate([PathResolveService.getCradleToGateLifecycleAnalysisHistoryPathUnit(
          true, packagingId, packagingVersionId, analysisVersionsId[0] ?? '')]);
        break;
      }
      case AnalysisType.Lifecycle: {
        this.router.navigate([PathResolveService.getLifecycleAnalysisHistoryPathUnit(
          true, packagingId, packagingVersionId, analysisVersionsId[0] ?? '')]);
        break;
      }
      case AnalysisType.ExpenseCalculation: {
        this.router.navigate([PathResolveService.getExpenseCalculationHistoryPathUnit(
          true, packagingId, packagingVersionId, analysisVersionsId[2], analysisVersionsId[0], analysisVersionsId[1])]);
        break;
      }
    }
  }

  navigateToComparison(listOfIdsToCompare: number[]) {
    this.router.navigate([PathResolveService.getCompareRecyclabilityAnalysisPathUnit(true, listOfIdsToCompare)]);
  }

  navigateToPackagingUnitPreview(candidateId: number, isChange = false, seePackagingUnitBeforeChange = false) {
    this.router.navigate([PathResolveService.getPackagingUnitPreviewPath(true, candidateId, isChange, seePackagingUnitBeforeChange)]);
  }
}
